module.exports = function buildConnectChatgptToYourBroker({ agentProofPoints }) {
  const SOURCE_VERIFIED_DATE = '2026-08-14';

  return {
    path: '/guides/connect-chatgpt-to-your-broker',
    title: 'Connect ChatGPT to Your Broker | BotSpot MCP Guide',
    description:
      'Connect ChatGPT to BotSpot through OAuth MCP, verify your supported broker and account mode, and keep every trading action behind explicit approval.',
    pageType: 'article',
    lastmod: '2026-10-11',
    content: {
      eyebrow: 'ChatGPT broker connection guide',
      heading: 'Connect ChatGPT to your broker through BotSpot',
      summary:
        'Connect ChatGPT to your trading workflow with BotSpot. Research markets, review strategies and inspect backtests through an OAuth MCP connection. Your supported broker connects inside BotSpot, and trading actions require separate approval.',
      primaryAction: {
        label: 'Open ChatGPT setup',
        url: '/agents#agents-client-chatgpt',
      },
      secondaryAction: {
        label: 'View supported brokers',
        url: '/brokers',
      },
      proofPoints: agentProofPoints,
      sections: [
        {
          heading: '1. Understand the connection boundary',
          paragraphs: [
            'ChatGPT does not connect directly to your broker in this workflow. ChatGPT connects to BotSpot, and BotSpot connects separately to a supported brokerage account.',
            'Keep broker credentials, API keys, private keys, and passwords out of ChatGPT prompts. Use BotSpot broker settings for brokerage authentication and BotSpot OAuth for the ChatGPT connection.',
          ],
        },
        {
          heading: '2. Connect a supported broker inside BotSpot',
          paragraphs: [
            'Open BotSpot Broker Connections and select the broker, authentication method, and paper or live mode supported by your account. Broker support, asset classes, authentication methods, and modes can change, so verify the current broker page before continuing.',
            'The current BotSpot manifest lists Alpaca, Charles Schwab, Tradier, Tradovate, Kraken, Coinbase, WEEX, Bitunix, and TopstepX. These connections do not support identical assets or account modes.',
          ],
          bullets: [
            'Alpaca supports listed stock, options, and crypto workflows with paper and live modes.',
            'Tradier supports listed stock and options workflows. Mode availability differs between OAuth and API-key connections.',
            'Tradovate and TopstepX cover listed futures workflows with paper and live modes.',
            'Charles Schwab is listed for live stock and options workflows.',
            'Kraken, Coinbase, WEEX, and Bitunix are listed for live crypto workflows.',
            'Regional eligibility, broker permissions, market-data access, and product approval still apply.',
          ],
        },
        {
          heading: '3. Add BotSpot to ChatGPT',
          paragraphs: [
            'Use ChatGPT on the web. Developer mode and custom connection availability can depend on your ChatGPT account, workspace role, and administrator policy.',
          ],
          bullets: [
            'Open ChatGPT Settings and enable Developer mode under Security and login when required.',
            'Open ChatGPT Plugins, select the plus button, and create a new MCP connection.',
            'Name the connection BotSpot.',
            'Choose Server URL and enter https://mcp.botspot.trade/mcp.',
            'Create the connection and let ChatGPT discover BotSpot authentication. Do not paste a BotSpot API key into ChatGPT.',
            'Complete BotSpot login and consent when prompted, then review the discovered tools and confirmation behavior.',
            'Start a new chat and enable BotSpot from the tools menu.',
          ],
        },
        {
          heading: '4. Validate read-only access first',
          paragraphs: [
            'Begin with requests that inspect data without changing broker or deployment state. Confirm that ChatGPT reaches the intended BotSpot user, brokerage connection, account, and trading mode.',
            'Do not infer live readiness from a successful MCP connection. MCP connectivity proves tool access, not broker permissions, strategy correctness, order eligibility, or execution quality.',
          ],
          bullets: [
            'Ask BotSpot to list available strategies without changing anything.',
            'Request an existing backtest and inspect its assumptions.',
            'Ask which broker and account mode are connected.',
            'Confirm whether the intended asset and product are supported.',
            'Use paper mode first where the broker connection supports it.',
          ],
        },
        {
          heading: '5. Keep trading actions behind explicit approval',
          paragraphs: [
            'When direct trading is enabled, ChatGPT can ask BotSpot to prepare a supported one-time order. BotSpot presents the action for review and explicit confirmation before submission.',
            'MCP-driven start, stop, update, and trade requests use action-specific authorization. BotSpot checks ownership and may require a fresh login. A recent login can reduce repeated credential prompts, but it never approves or batches actions automatically.',
            'Review symbol, side, quantity, order type, account, broker, and paper or live mode on the BotSpot authorization page. Reject any action that differs from your request.',
          ],
        },
        {
          heading: '6. Use prompts with clear operating boundaries',
          paragraphs: [
            'Tell ChatGPT which BotSpot tool boundary applies. Separate research, strategy changes, backtesting, deployment, and trading instead of combining them into one broad instruction.',
          ],
          bullets: [
            '"Using BotSpot, show my connected broker and account mode. Do not place orders or change deployments."',
            '"Review this strategy and its latest backtest. Identify assumptions, but do not revise or deploy it."',
            '"Prepare an order proposal for review. Do not submit anything without the BotSpot approval page."',
            '"Use only my paper account for this test. Stop if paper mode is unavailable."',
          ],
        },
        {
          heading: '7. Troubleshoot the connection',
          bullets: [
            'Missing Developer mode: check ChatGPT account access and workspace administrator policy.',
            'Connection failure: verify the complete https://mcp.botspot.trade/mcp URL, including the /mcp path.',
            'OAuth remains incomplete: remove the failed connection, add it again, and finish BotSpot login and consent.',
            'Tools appear stale: refresh the connection metadata or start a new ChatGPT conversation.',
            'Broker action is unavailable: verify the BotSpot broker connection, account mode, permissions, asset support, and authentication status.',
          ],
        },
        {
          heading: 'Security and trading risk',
          paragraphs: [
            'OAuth access does not make AI-generated instructions trustworthy. Prompt injection, ambiguous requests, stale context, software defects, broker restrictions, and market conditions can all produce unsafe or invalid actions.',
            'Never paste brokerage secrets into chat. Inspect every authorization request and monitor positions, orders, balances, and deployed strategies through the broker and BotSpot.',
          ],
        },
      ],
      faq: [
        {
          question: 'Does ChatGPT connect directly to my broker?',
          answer:
            'No. ChatGPT connects to BotSpot through OAuth MCP. Your supported brokerage account connects separately inside BotSpot.',
        },
        {
          question: 'Should I paste my BotSpot or broker API key into ChatGPT?',
          answer:
            'No. ChatGPT uses BotSpot OAuth for this connection. Enter brokerage credentials only through the appropriate BotSpot broker connection flow.',
        },
        {
          question: 'Can ChatGPT place a live trade automatically?',
          answer:
            'BotSpot supports approved one-time trading actions when direct trading is enabled. The user must review and explicitly approve the action. Automated strategy deployment is a separate reviewed workflow.',
        },
        {
          question: 'Which brokers can I use?',
          answer:
            'Use the current BotSpot broker directory and public manifest. Each broker supports different assets, authentication methods, paper or live modes, regions, and account permissions.',
        },
        {
          question: 'Why is Developer mode missing in ChatGPT?',
          answer:
            'OpenAI documents that Developer mode availability can depend on account access and workspace policy. A workspace administrator may need to enable the required access.',
        },
      ],
      relatedLinks: [
        {
          label: 'Connect Claude and Claude Code to your broker',
          url: '/guides/connect-claude-to-your-broker',
        },
        { label: 'Connect Codex to your broker', url: '/guides/connect-codex-to-your-broker' },
        {
          label: 'Open ChatGPT MCP setup',
          url: '/agents#agents-client-chatgpt',
        },
        {
          label: 'Explore supported brokers',
          url: '/brokers',
        },
        {
          label: 'Build an AI trading bot',
          url: '/guides/how-to-build-an-ai-trading-bot',
        },
        {
          label: 'Compare paper and live trading',
          url: '/guides/paper-trading-vs-live-trading',
        },
      ],
      sources: [
        {
          label: 'BotSpot MCP for Agents',
          url: '/agents',
        },
        {
          label: 'BotSpot broker connection manifest',
          url: '/.well-known/botspot-manifest.json',
        },
        {
          label: 'BotSpot Terms of Service',
          url: '/terms',
        },
        {
          label: 'OpenAI: Connect and test an MCP plugin',
          url: 'https://developers.openai.com/plugins/deploy/connect-chatgpt',
        },
        {
          label: 'OpenAI plugin security and privacy guidance',
          url: 'https://developers.openai.com/plugins/guides/security-privacy',
        },
      ],
      verifiedAt: SOURCE_VERIFIED_DATE,
    },
  };
};
